import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { cachedRequest, invalidateCache } from '../utils/apiCache';

// Use relative URLs in production to avoid CORS issues
// Native Capacitor app must use absolute URL
const API_URL = Capacitor.isNativePlatform()
  ? 'https://protrener.app/api'
  : (process.env.NODE_ENV === 'production' 
    ? '/api' 
    : (process.env.REACT_APP_API_URL || 'http://localhost:5000/api'));

// Get auth token from localStorage
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ============================================
// PUBLIC SHOP API
// ============================================

// Cached for 5 minutes (product catalog doesn't change frequently)
export const getPublicProducts = async () => {
  return cachedRequest(
    () => axios.get(`${API_URL}/shop/public/products`),
    {
      method: 'GET',
      url: `${API_URL}/shop/public/products`,
      cache: true,
      ttl: 5 * 60 * 1000, // 5 minutes
      deduplicate: true
    }
  ).then(response => response.data);
};

// Cached for 5 minutes
export const getPublicProduct = async (productId) => {
  return cachedRequest(
    () => axios.get(`${API_URL}/shop/public/products/${productId}`),
    {
      method: 'GET',
      url: `${API_URL}/shop/public/products/${productId}`,
      cache: true,
      ttl: 5 * 60 * 1000, // 5 minutes
      deduplicate: true
    }
  ).then(response => response.data);
};

// Cached for 5 minutes - Get all trainers with their products for shop browse page
export const getTrainersWithProducts = async () => {
  return cachedRequest(
    () => axios.get(`${API_URL}/shop/public/trainers-with-products`),
    {
      method: 'GET',
      url: `${API_URL}/shop/public/trainers-with-products`,
      cache: true,
      ttl: 5 * 60 * 1000, // 5 minutes
      deduplicate: true
    }
  ).then(response => response.data);
};

export const createGuestCheckout = async (productId, guestInfo, paymentMethod) => {
  const response = await axios.post(`${API_URL}/shop/public/checkout/guest`, {
    productId,
    guestInfo,
    paymentMethod
  });
  return response.data;
};

// ============================================
// MEMBER SHOP API
// ============================================

export const getProducts = async () => {
  const response = await axios.get(`${API_URL}/shop/products`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const createStripeCheckout = async (productId) => {
  const response = await axios.post(
    `${API_URL}/shop/purchase/stripe`,
    { productId },
    { headers: getAuthHeader() }
  );
  return response.data;
};

export const createCashOrder = async (productId) => {
  const response = await axios.post(
    `${API_URL}/shop/purchase/cash`,
    { productId },
    { headers: getAuthHeader() }
  );
  return response.data;
};

export const getMyPurchases = async () => {
  const response = await axios.get(`${API_URL}/shop/my-purchases`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getMyAccess = async () => {
  const response = await axios.get(`${API_URL}/shop/my-access`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getPendingCashOrders = async () => {
  const response = await axios.get(`${API_URL}/shop/pending-cash-orders`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getDownloadUrl = async (token) => {
  const response = await axios.get(`${API_URL}/shop/download/${token}`);
  return response.data;
};

// ============================================
// TRAINER SHOP API
// ============================================

export const getTrainerProducts = async () => {
  const response = await axios.get(`${API_URL}/shop/trainer/products`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const createProduct = async (productData) => {
  const response = await axios.post(
    `${API_URL}/shop/trainer/products`,
    productData,
    { headers: getAuthHeader() }
  );
  // Invalidate all product caches
  invalidateCache([
    `${API_URL}/shop/public/products`,
    `${API_URL}/shop/products`,
    `${API_URL}/shop/trainer/products`
  ]);
  return response.data;
};

export const updateProduct = async (productId, productData) => {
  const response = await axios.put(
    `${API_URL}/shop/trainer/products/${productId}`,
    productData,
    { headers: getAuthHeader() }
  );
  // Invalidate product caches
  invalidateCache([
    `${API_URL}/shop/public/products`,
    `${API_URL}/shop/products`,
    `${API_URL}/shop/trainer/products`,
    `${API_URL}/shop/public/products/${productId}`
  ]);
  return response.data;
};

export const deleteProduct = async (productId) => {
  const response = await axios.delete(
    `${API_URL}/shop/trainer/products/${productId}`,
    { headers: getAuthHeader() }
  );
  // Invalidate product caches
  invalidateCache([
    `${API_URL}/shop/public/products`,
    `${API_URL}/shop/products`,
    `${API_URL}/shop/trainer/products`,
    `${API_URL}/shop/public/products/${productId}`
  ]);
  return response.data;
};

export const uploadProductPDF = async (productId, file) => {
  const formData = new FormData();
  formData.append('pdf', file);
  
  const response = await axios.post(
    `${API_URL}/shop/trainer/products/${productId}/upload-pdf`,
    formData,
    {
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'multipart/form-data'
      }
    }
  );
  return response.data;
};

export const uploadProductImage = async (productId, file) => {
  const formData = new FormData();
  formData.append('image', file);
  
  const response = await axios.post(
    `${API_URL}/shop/trainer/products/${productId}/upload-image`,
    formData,
    {
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'multipart/form-data'
      }
    }
  );
  return response.data;
};

export const getTrainerPendingCash = async () => {
  const response = await axios.get(`${API_URL}/shop/trainer/pending-cash`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const confirmCashPayment = async (purchaseId, notes) => {
  const response = await axios.post(
    `${API_URL}/shop/trainer/confirm-cash/${purchaseId}`,
    { notes },
    { headers: getAuthHeader() }
  );
  return response.data;
};

export const getTrainerSales = async (params = {}) => {
  const response = await axios.get(`${API_URL}/shop/trainer/sales`, {
    headers: getAuthHeader(),
    params
  });
  return response.data;
};

const syncStripePayments = async () => {
  const response = await axios.post(`${API_URL}/shop/trainer/sync-stripe-payments`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const refundTrainerPurchase = async (purchaseId) => {
  const response = await axios.post(
    `${API_URL}/shop/trainer/purchase/${purchaseId}/refund`,
    {},
    { headers: getAuthHeader() }
  );
  return response.data;
};

export const getTrainerWorkoutPlans = async (trainerId = null) => {
  const response = await axios.get(`${API_URL}/shop/trainer/workout-plans`, {
    headers: getAuthHeader(),
    params: trainerId ? { trainerId } : {}
  });
  return response.data;
};

export const getTrainerHiitWorkouts = async (trainerId = null) => {
  const response = await axios.get(`${API_URL}/shop/trainer/hiit-workouts`, {
    headers: getAuthHeader(),
    params: trainerId ? { trainerId } : {}
  });
  return response.data;
};

// ============================================
// PRODUCT FILE MANAGEMENT API
// ============================================

/**
 * Upload files to a product (PDF, Excel, documents)
 * @param {string} productId - Product ID
 * @param {File[]} files - Array of files to upload
 * @returns {Promise<{message: string, files: Array}>}
 */
export const uploadProductFiles = async (productId, files) => {
  // Upload each file straight to object storage via a presigned PUT (bypassing
  // our nginx/Node body-size limit), then persist the metadata server-side.
  const { uploadViaPresignedUrl } = await import('../utils/presignedUpload');

  const meta = [];
  for (const file of files) {
    const fileUrl = await uploadViaPresignedUrl(file, { kind: 'product-file', productId });
    meta.push({ fileUrl, originalName: file.name, fileSize: file.size });
  }

  const response = await axios.post(
    `${API_URL}/shop/trainer/products/${productId}/files/finalize`,
    { files: meta },
    { headers: getAuthHeader() }
  );
  return response.data;
};

/**
 * Get all files for a product
 * @param {string} productId - Product ID
 * @returns {Promise<{files: Array}>}
 */
export const getProductFiles = async (productId) => {
  const response = await axios.get(
    `${API_URL}/shop/trainer/products/${productId}/files`,
    { headers: getAuthHeader() }
  );
  return response.data;
};

/**
 * Delete a file from a product
 * @param {string} productId - Product ID
 * @param {string} fileId - File ID
 * @returns {Promise<{message: string}>}
 */
export const deleteProductFile = async (productId, fileId) => {
  const response = await axios.delete(
    `${API_URL}/shop/trainer/products/${productId}/files/${fileId}`,
    { headers: getAuthHeader() }
  );
  return response.data;
};

/**
 * Reorder files for a product
 * @param {string} productId - Product ID
 * @param {Array<{id: string, displayOrder: number}>} fileOrder - New file order
 * @returns {Promise<{files: Array}>}
 */
export const reorderProductFiles = async (productId, fileOrder) => {
  const response = await axios.put(
    `${API_URL}/shop/trainer/products/${productId}/files/reorder`,
    { fileOrder },
    { headers: getAuthHeader() }
  );
  return response.data;
};

/**
 * Get download URL for a purchased product file
 * @param {string} fileId - File ID
 * @returns {Promise<{downloadUrl: string, fileName: string, token: string}>}
 */
export const getFileDownload = async (fileId) => {
  const response = await axios.get(
    `${API_URL}/shop/download/file/${fileId}`,
    { headers: getAuthHeader() }
  );
  return response.data;
};

// ============================================
// ADMIN SHOP API
// ============================================

export const getAdminProducts = async () => {
  const response = await axios.get(`${API_URL}/shop/admin/products`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const createAdminProduct = async (productData) => {
  const response = await axios.post(
    `${API_URL}/shop/admin/products`,
    productData,
    { headers: getAuthHeader() }
  );
  return response.data;
};

export const getAdminPurchases = async () => {
  const response = await axios.get(`${API_URL}/shop/admin/purchases`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getAdminPendingCash = async () => {
  const response = await axios.get(`${API_URL}/shop/admin/pending-cash`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const adminConfirmCash = async (purchaseId, notes) => {
  const response = await axios.post(
    `${API_URL}/shop/admin/confirm-cash/${purchaseId}`,
    { notes },
    { headers: getAuthHeader() }
  );
  return response.data;
};

export const refundPurchase = async (purchaseId) => {
  const response = await axios.post(
    `${API_URL}/shop/admin/purchase/${purchaseId}/refund`,
    {},
    { headers: getAuthHeader() }
  );
  return response.data;
};

export const getAdminAnalytics = async () => {
  const response = await axios.get(`${API_URL}/shop/admin/analytics`, {
    headers: getAuthHeader()
  });
  return response.data;
};

const shopApi = {
  // Public
  getPublicProducts,
  getPublicProduct,
  getTrainersWithProducts,
  
  // Member
  getProducts,
  createStripeCheckout,
  createCashOrder,
  getMyPurchases,
  getMyAccess,
  getPendingCashOrders,
  getDownloadUrl,
  
  // Trainer
  getTrainerProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  uploadProductPDF,
  uploadProductImage,
  getTrainerPendingCash,
  confirmCashPayment,
  getTrainerSales,
  getTrainerWorkoutPlans,
  getTrainerHiitWorkouts,
  refundTrainerPurchase,

  // Product Files
  uploadProductFiles,
  getProductFiles,
  deleteProductFile,
  reorderProductFiles,
  getFileDownload,
  
  // Admin
  getAdminProducts,
  createAdminProduct,
  getAdminPurchases,
  getAdminPendingCash,
  adminConfirmCash,
  refundPurchase,
  getAdminAnalytics,
  syncStripePayments
};

export default shopApi;
